import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { SegmentedPills, StatusPill } from './systemKit';
import PremiumLineChart, { fmtDateShort } from './PremiumLineChart';
import { ContextCard } from '../InventoryTrends';
import { generateInsight } from '../lib/inventoryInsight';
import { CONSECUTIVE_RED_THRESHOLD } from '../lib/invoiceDiff';

// ─── InvoiceReviewItemDrillIn ────────────────────────────────────────────────
//
// Per-line drill-in panel for the Invoice Review modal. Operator clicks a
// flagged item row ("Honey Syrup +8.4%") and this slide-over pops in from
// the right, showing the price/volume trend for that food_item with the
// CURRENT invoice highlighted on both the chart and the history table.
//
// Architectural parallel: this is a slimmed sibling of `ItemDrawerBody` in
// InventoryTrends.js. We don't reuse ItemDrawerBody directly because:
//   - Its headline is window-relative ("$1.2k this period across 3 orders").
//     The drill-in's headline is invoice-relative ("$/lb on this delivery
//     was 8.4% above the prior buy").
//   - It carries shrinkflation-alert UI we don't need on this surface
//     (the toast already escalated us — the operator's already in the
//     review flow).
//   - Its highlight axis is "in current period" (greyed prior rows).
//     The drill-in's highlight axis is "the current invoice" (one row).
//
// Shared bits we DO reuse:
//   - PremiumLineChart (extended with highlightDate + markers props)
//   - ContextCard (exported from InventoryTrends.js)
//   - generateInsight (pure helper; works fine on whatever lines we feed)
//
// ── Close behavior ───────────────────────────────────────────────────────
// Per the new "high-stakes review surface" convention in DESIGN_SYSTEM.md,
// this panel closes ONLY via its X button or ESC. The dim backdrop is
// purely visual — clicking it does nothing. Wired via the parent modal,
// which holds drill-in state and intercepts ESC at capture phase.
//
// ── Date window ──────────────────────────────────────────────────────────
// 90 days centered on the current invoice's date, with a 12-month auto-
// extend if the 90d window yields fewer than 5 data points. Bounded by
// "today" on the upper end so we don't ask for future invoice data we'll
// never have.

const WINDOW_DAYS = 90;
const FALLBACK_DAYS = 365;
const MIN_POINTS = 5;

function addDays(iso, days) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function todayIso() {
  return new Date().toISOString().split('T')[0];
}

function fmtPack(line) {
  if (!line || line.pack_count == null) return null;
  const pc = Number(line.pack_count);
  const pw = line.piece_weight ? String(line.piece_weight).trim() : null;
  if (pc === 1 && pw) return pw;
  return pw ? `${pc}\u00D7${pw}` : `${pc}`;
}

// computeLineVolumeLbs — single source of truth for "how many lbs did this
// invoice line represent". Returns { lbs, estimated }; lbs is null when
// there isn't enough data to compute.
//
// Two paths:
//   - Catch-weight items: each line records its OWN actual weight. Trust
//     line.total_weight when present; if an older invoice didn't capture
//     it, fall back to qty x per-case weight and flag estimated:true so
//     the tooltip + history table can show "(estimated)".
//   - Non-catch-weight items: per-case weight is constant, total purchased
//     weight = qty x weight_per_case. We deliberately ignore line.total_weight
//     here because the upstream parser populates that column with the
//     per-case weight (a constant per item) instead of qty x per-case weight,
//     producing a flat-line chart at pack weight regardless of order size.
//
// TODO(parser): the upstream root cause lives in api/twilio-webhook.js +
// api/pdf-poll.js where the prompt describes total_weight_lbs as "lbs per
// case" rather than "lbs per invoice line". When that's fixed and the
// column is repopulated, the non-catch-weight branch can simply use
// line.total_weight too and this helper collapses to one path.
function computeLineVolumeLbs(line, weightPerCase, isCatchWeight) {
  if (!line) return { lbs: null, estimated: false };
  const qty = Number(line.quantity);
  const wpc = Number(weightPerCase);
  if (isCatchWeight) {
    const tw = Number(line.total_weight);
    if (line.total_weight != null && isFinite(tw) && tw > 0) {
      return { lbs: tw, estimated: false };
    }
    if (isFinite(qty) && qty > 0 && isFinite(wpc) && wpc > 0) {
      return { lbs: qty * wpc, estimated: true };
    }
    return { lbs: null, estimated: false };
  }
  if (isFinite(qty) && qty > 0 && isFinite(wpc) && wpc > 0) {
    return { lbs: qty * wpc, estimated: false };
  }
  return { lbs: null, estimated: false };
}

// Per-metric "do we have at least 2 plottable points?" check. Drives both
// the smart default selection and the disabled state on metric pills —
// the operator sees only the metrics that will actually render a chart.
// We require ≥2 points because a single dot doesn't tell a trend story.
function hasPlottableData(lines, metricKey, weightPerCase, isCatchWeight) {
  if (!lines || lines.length === 0) return false;
  let count = 0;
  for (const l of lines) {
    let v = null;
    if (metricKey === 'perLb') {
      v = l.price_per_lb != null && Number(l.price_per_lb) > 0 ? Number(l.price_per_lb) : null;
    } else if (metricKey === 'volume') {
      const { lbs } = computeLineVolumeLbs(l, weightPerCase, isCatchWeight);
      v = lbs;
    } else if (metricKey === 'qty') {
      v = l.quantity != null && isFinite(Number(l.quantity)) ? Number(l.quantity) : null;
    } else if (metricKey === 'spend') {
      v = l.line_total != null && isFinite(Number(l.line_total)) ? Number(l.line_total) : null;
    }
    if (v != null && isFinite(v)) {
      count += 1;
      if (count >= 2) return true;
    }
  }
  return false;
}

// Smart-default metric. $/lb wins when available because that's the
// answer to "is the same item's price moving?" — the question that
// drove the operator into the drill-in. Falls through to volume, then
// qty, then spend, when $/lb is unplottable (e.g., counted-only items
// where price_per_lb is null on every row). Last resort: keep $/lb so
// the existing "No data to plot" empty state renders cleanly rather
// than silently picking a different unprintable metric.
function pickDefaultMetric(lines, weightPerCase, isCatchWeight) {
  if (hasPlottableData(lines, 'perLb', weightPerCase, isCatchWeight)) return 'perLb';
  if (hasPlottableData(lines, 'volume', weightPerCase, isCatchWeight)) return 'volume';
  if (hasPlottableData(lines, 'qty', weightPerCase, isCatchWeight)) return 'qty';
  if (hasPlottableData(lines, 'spend', weightPerCase, isCatchWeight)) return 'spend';
  return 'perLb';
}

export default function InvoiceReviewItemDrillIn({
  open,
  onClose,
  companyId,
  foodItemId,
  itemName,
  itemCategory,
  isCatchWeight = false,
  currentInvoiceId,
  currentInvoiceDate,
  parseDiff = null,
}) {
  const [lines, setLines] = useState(null);
  // weight_per_case + is_catch_weight pulled from food_items at open time.
  // We fetch these in the component (rather than threading another prop
  // through every caller) because the volume math depends on the canonical
  // food_items row and the 3 callers of this component shouldn't all need
  // to fetch + pass through the same fields. is_catch_weight from props
  // takes precedence so existing call sites keep working — the fetched
  // value just supplies weight_per_case.
  const [weightPerCase, setWeightPerCase] = useState(null);
  // metric starts at null — the smart-default effect picks the right
  // initial value once `lines` arrives. Until then we don't render the
  // chart (avoids a one-frame flash of "No data" on counted-only items).
  const [metric, setMetric] = useState(null);
  // Tracks whether we've already auto-picked the default for this open.
  // Once the operator manually clicks a metric pill we stop auto-picking.
  const [metricAutoPicked, setMetricAutoPicked] = useState(false);
  const [windowDays, setWindowDays] = useState(WINDOW_DAYS);

  // Reset on open so a fresh drill-in on a different item starts clean.
  useEffect(() => {
    if (!open) return;
    setLines(null);
    setWeightPerCase(null);
    setMetric(null);
    setMetricAutoPicked(false);
    setWindowDays(WINDOW_DAYS);
  }, [open, foodItemId]);

  // Fetch food_items.weight_per_case once per drill-in open. Drives the
  // volume math (qty x weight_per_case) and the disabled state on the
  // Volume metric pill when an item has no recorded pack weight.
  useEffect(() => {
    if (!open || !companyId || !foodItemId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('food_items')
        .select('weight_per_case')
        .eq('id', foodItemId)
        .maybeSingle();
      if (cancelled) return;
      const wpc = data?.weight_per_case != null ? Number(data.weight_per_case) : null;
      setWeightPerCase(wpc != null && isFinite(wpc) && wpc > 0 ? wpc : null);
    })();
    return () => { cancelled = true; };
  }, [open, companyId, foodItemId]);

  // Fetch lines for the food_item over the window.
  useEffect(() => {
    if (!open || !companyId || !foodItemId || !currentInvoiceDate) return;
    let cancelled = false;
    (async () => {
      const start = addDays(currentInvoiceDate, -windowDays);
      const end = todayIso();
      const { data } = await supabase.from('invoice_line_items')
        .select('id, invoice_id, invoice_date, vendor_name, quantity, unit, total_weight, price_per_lb, unit_price, line_total, pack_count, piece_weight, parse_diff')
        .eq('company_id', companyId)
        .eq('food_item_id', foodItemId)
        .gte('invoice_date', start)
        .lte('invoice_date', end)
        .order('invoice_date', { ascending: true });
      if (cancelled) return;
      const fetched = data || [];
      // Auto-extend to 12 months if the 90d window is sparse — still want
      // a recognizable trend shape on the chart.
      if (fetched.length < MIN_POINTS && windowDays < FALLBACK_DAYS) {
        setWindowDays(FALLBACK_DAYS);
        return;
      }
      setLines(fetched);
    })();
    return () => { cancelled = true; };
  }, [open, companyId, foodItemId, currentInvoiceDate, windowDays]);

  const useWeight = useMemo(() => {
    return (lines || []).some(l => l.total_weight != null) || isCatchWeight;
  }, [lines, isCatchWeight]);

  // Per-metric availability — drives both smart-default selection and the
  // disabled state on the metric pills. Memoized; recomputed when the
  // window's lines or useWeight change.
  const metricAvailability = useMemo(() => ({
    perLb:  hasPlottableData(lines, 'perLb',  weightPerCase, isCatchWeight),
    volume: hasPlottableData(lines, 'volume', weightPerCase, isCatchWeight),
    qty:    hasPlottableData(lines, 'qty',    weightPerCase, isCatchWeight),
    spend:  hasPlottableData(lines, 'spend',  weightPerCase, isCatchWeight),
  }), [lines, weightPerCase, isCatchWeight]);

  // One-shot smart-default after `lines` first lands. We don't tweak the
  // operator's choice once they pick something manually — only the
  // initial pick is automated. metricAutoPicked latches once the default
  // fires so a re-render with the same lines doesn't bounce.
  //
  // Also depends on weightPerCase landing — picking the volume default
  // before the food_items fetch resolves would force the user onto $/lb
  // unnecessarily. We require both before defaulting.
  useEffect(() => {
    if (!lines || metricAutoPicked) return;
    setMetric(pickDefaultMetric(lines, weightPerCase, isCatchWeight));
    setMetricAutoPicked(true);
  }, [lines, weightPerCase, isCatchWeight, metricAutoPicked]);

  // currLines = lines on/after the current invoice's date (typically
  // just the current invoice itself). priorLines = everything before.
  // This is the split generateInsight expects, repurposed: "current"
  // becomes "this delivery and onward", "prior" becomes "history."
  const { currLines, priorLines } = useMemo(() => {
    const all = lines || [];
    const curr = all.filter(l => l.invoice_date >= currentInvoiceDate);
    const prior = all.filter(l => l.invoice_date < currentInvoiceDate);
    return { currLines: curr, priorLines: prior };
  }, [lines, currentInvoiceDate]);

  const insight = useMemo(() => {
    if (!lines) return null;
    return generateInsight({
      item: { is_catch_weight: isCatchWeight },
      currLines,
      priorLines,
      alert: null,
      useWeight,
    });
  }, [lines, currLines, priorLines, isCatchWeight, useWeight]);

  // Stats for the 2×2 context grid. Mirrors ItemDrawerBody.stats but
  // computed over the full window (not period-split) since this surface
  // shows "the item's life around this delivery", not "this period vs
  // prior period."
  const stats = useMemo(() => {
    const all = lines || [];
    const perLbVals = all.map(l => Number(l.price_per_lb)).filter(v => v > 0);
    const avgPerLb = perLbVals.length ? perLbVals.reduce((a, b) => a + b, 0) / perLbVals.length : null;
    const currentLine = all.find(l => l.invoice_id === currentInvoiceId);
    const currentPerLb = currentLine?.price_per_lb != null ? Number(currentLine.price_per_lb) : null;
    const currentPack = fmtPack(currentLine);
    const distinctPacks = Array.from(new Set(all.map(fmtPack).filter(Boolean)));

    const vCounts = {};
    for (const l of all) {
      if (l.vendor_name) vCounts[l.vendor_name] = (vCounts[l.vendor_name] || 0) + 1;
    }
    const vSorted = Object.entries(vCounts).sort((a, b) => b[1] - a[1]);
    const topVendor = vSorted[0] ? vSorted[0][0] : null;

    // Cadence: average gap between distinct invoice dates in window.
    const dates = Array.from(new Set(all.map(l => l.invoice_date))).sort();
    let avgGap = null;
    if (dates.length >= 2) {
      const gaps = [];
      for (let i = 1; i < dates.length; i++) {
        const d0 = new Date(dates[i - 1] + 'T00:00:00').getTime();
        const d1 = new Date(dates[i] + 'T00:00:00').getTime();
        gaps.push((d1 - d0) / 86400000);
      }
      avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
    }
    return { avgPerLb, currentPerLb, currentPack, distinctPacks, topVendor, avgGap, dates };
  }, [lines, currentInvoiceId]);

  // Chart series — one point per invoice line, by metric. Each point
  // carries extras (qty, lbs, packNote, estimated, vendor) for the
  // multi-line tooltip. PremiumLineChart only reads `date` and `value`
  // for layout; extras are passed to the optional tooltipFormat callback.
  const chartSeries = useMemo(() => {
    return (lines || []).map(l => {
      const { lbs, estimated } = computeLineVolumeLbs(l, weightPerCase, isCatchWeight);
      const qty = l.quantity != null && isFinite(Number(l.quantity)) ? Number(l.quantity) : null;
      let value = null;
      if (metric === 'volume')      value = lbs;
      else if (metric === 'qty')    value = qty;
      else if (metric === 'perLb')  value = l.price_per_lb != null ? Number(l.price_per_lb) : null;
      else if (metric === 'spend')  value = Number(l.line_total) || 0;
      return {
        date: l.invoice_date,
        value,
        _qty: qty,
        _qtyUnit: l.unit || 'case',
        _lbs: lbs,
        _estimated: estimated,
        _packNote: fmtPack(l),
        _vendor: l.vendor_name,
        _spend: Number(l.line_total) || 0,
      };
    }).filter(p => p.value != null && isFinite(p.value));
  }, [lines, metric, weightPerCase, isCatchWeight]);

  const yFormat = (v) => {
    if (metric === 'volume') return `${Math.round(v)} lb`;
    if (metric === 'qty')    return v % 1 === 0 ? String(v) : v.toFixed(1);
    if (metric === 'perLb')  return `$${v.toFixed(2)}`;
    if (metric === 'spend')  return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`;
    return String(v);
  };

  // tooltipFormat — multi-line hover content for the Volume / Qty tabs so
  // the operator can see qty + total volume + pack context together. Other
  // tabs use the chart's default tooltip ([date, yFormat(value)]).
  const tooltipFormat = (point) => {
    if (!point) return null;
    if (metric === 'volume') {
      const lines = [fmtDateShort(point.date)];
      if (point._qty != null) {
        const unit = point._qtyUnit || 'case';
        const plural = point._qty === 1 ? '' : 's';
        lines.push(`${point._qty} ${unit}${plural}`);
      }
      if (point._lbs != null) {
        lines.push(`${Math.round(point._lbs)} lb${point._estimated ? ' (estimated)' : ''}`);
      }
      if (point._packNote) lines.push(`pack ${point._packNote}`);
      return lines;
    }
    if (metric === 'qty') {
      const lines = [fmtDateShort(point.date)];
      const unit = point._qtyUnit || 'case';
      const plural = point._qty === 1 ? '' : 's';
      lines.push(`${point.value} ${unit}${plural}`);
      if (point._lbs != null) {
        lines.push(`${Math.round(point._lbs)} lb total${point._estimated ? ' (estimated)' : ''}`);
      }
      if (point._vendor) lines.push(String(point._vendor).replace(/\n/g, ' / '));
      return lines;
    }
    return null;  // perLb / spend → fall back to default tooltip
  };

  // packChangeFromDiff — the {from, to} block authoritatively derived from
  // the line's parse_diff. We accept both `pack_changed` (canonical, what
  // computeLineDiff produces) and `pack_change` (defensive — hand-rolled
  // test JSON sometimes uses the singular). Returns null when the diff
  // doesn't describe a real change (from === to, or one side missing).
  const packChangeFromDiff = useMemo(() => {
    const block = parseDiff?.pack_changed || parseDiff?.pack_change;
    if (!block || !block.from || !block.to) return null;
    if (String(block.from) === String(block.to)) return null;
    return { from: String(block.from), to: String(block.to) };
  }, [parseDiff]);

  // Pinned chart marker — on pack_changed lines we want the operator to
  // see the cause-effect together: the dot on the chart sits where the
  // pack changed, and the marker calls it out. Authoritative source is
  // parse_diff (computed by FoodPriceTracker at upload time); the chart
  // doesn't try to re-derive pack changes from sparse historical columns.
  const chartMarkers = useMemo(() => {
    if (!packChangeFromDiff) return [];
    return [{
      date: currentInvoiceDate,
      label: `Pack: ${packChangeFromDiff.from} \u2192 ${packChangeFromDiff.to}`,
      tone: 'amber',
    }];
  }, [packChangeFromDiff, currentInvoiceDate]);

  // Optional trend chip above the headline — surfaces the
  // consecutive_increases red-flag from the parse_diff so the operator
  // sees "3rd consecutive raise" without having to read the chart.
  const trendChip = useMemo(() => {
    const ci = parseDiff?.consecutive_increases || 0;
    if (parseDiff?.class === 'up' && ci >= CONSECUTIVE_RED_THRESHOLD) {
      return { tone: 'critical', text: `${ci}\u00D7 consecutive raise` };
    }
    if (parseDiff?.class === 'pack_changed') {
      return { tone: 'warning', text: 'Pack size changed' };
    }
    if (parseDiff?.class === 'new_item') {
      return { tone: 'neutral', text: 'First time we\'ve seen this' };
    }
    return null;
  }, [parseDiff]);

  if (!open) return null;

  // Range model for PremiumLineChart: divider lands on the current
  // invoice's date so the chart visually splits "history" from "this
  // delivery and after". Highlight + divider double-signal the same
  // moment but in complementary registers (one is positional, one is
  // the dot ring).
  const allDates = (lines || []).map(l => l.invoice_date).sort();
  const rangeStart = currentInvoiceDate;
  const rangeEnd = (allDates[allDates.length - 1] && allDates[allDates.length - 1] > currentInvoiceDate)
    ? allDates[allDates.length - 1]
    : currentInvoiceDate;
  const priorStart = allDates[0] || addDays(currentInvoiceDate, -windowDays);
  const priorEnd = priorLines.length > 0 ? priorLines[priorLines.length - 1].invoice_date : addDays(currentInvoiceDate, -1);

  const range = { start: rangeStart, end: rangeEnd };
  const priorRange = { start: priorStart, end: priorEnd };

  return (
    <>
      {/* Backdrop — visual cover only. NO onClick. Per high-stakes
          review-surface close convention, this panel only dismisses
          via X / ESC / cascading parent close. */}
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 75, // above the InvoiceReviewModal (70), below this panel
        }}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Item drill-in"
        className="no-print"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: 560,
          background: 'var(--bg-card)',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.35)',
          zIndex: 80,
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          className="px-5 py-4 flex items-start justify-between gap-3"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="min-w-0 flex-1">
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: 'var(--text-secondary)',
              marginBottom: 4,
            }}>Item drill-in</div>
            <div className="text-base font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
              {itemName || 'Item'}
            </div>
            <div className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
              <span className="inline-flex items-center gap-2 flex-wrap">
                {itemCategory && <span>{itemCategory}</span>}
                {isCatchWeight && <StatusPill tone="neutral">catch-weight</StatusPill>}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close drill-in"
            className="text-lg px-1 rounded-md transition-colors"
            style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
          >
            {'\u2715'}
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {!lines && (
            <div className="text-sm" style={{ color: 'var(--text-muted)' }}>{'Loading\u2026'}</div>
          )}

          {lines && (
            <>
              {/* Trend chip + headline insight */}
              <div className="space-y-1.5">
                {trendChip && (
                  <div style={{
                    display: 'inline-block',
                    padding: '3px 8px',
                    borderRadius: 6,
                    fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    background: trendChip.tone === 'critical'
                      ? 'color-mix(in srgb, var(--text-critical) 12%, transparent)'
                      : trendChip.tone === 'warning'
                      ? 'color-mix(in srgb, var(--text-warning) 14%, transparent)'
                      : 'var(--bg-subtle)',
                    color: trendChip.tone === 'critical' ? 'var(--text-critical)'
                      : trendChip.tone === 'warning' ? 'var(--text-warning)'
                      : 'var(--text-secondary)',
                    border: '1px solid ' + (trendChip.tone === 'critical'
                      ? 'color-mix(in srgb, var(--text-critical) 35%, transparent)'
                      : trendChip.tone === 'warning'
                      ? 'color-mix(in srgb, var(--text-warning) 40%, transparent)'
                      : 'var(--border)'),
                  }}>
                    {trendChip.text}
                  </div>
                )}
                <div className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
                  {insight?.headline || 'Loading\u2026'}
                </div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {`Window: ${fmtDateShort(addDays(currentInvoiceDate, -windowDays))} \u2013 ${fmtDateShort(todayIso())}`}
                  {windowDays === FALLBACK_DAYS && (
                    <span style={{ marginLeft: 6, fontStyle: 'italic' }}>
                      (extended to 12 months \u2014 sparse history)
                    </span>
                  )}
                </div>
              </div>

              {/* 2×2 Context grid */}
              <div className="grid grid-cols-2 gap-3">
                <ContextCard
                  label="This invoice $/lb"
                  value={stats.currentPerLb != null ? `$${stats.currentPerLb.toFixed(2)}` : '\u2014'}
                  secondary={stats.avgPerLb != null
                    ? `window avg $${stats.avgPerLb.toFixed(2)}`
                    : 'no $/lb data'}
                  tone={parseDiff?.class === 'up' && (parseDiff?.consecutive_increases || 0) >= CONSECUTIVE_RED_THRESHOLD
                    ? 'critical'
                    : parseDiff?.class === 'up' ? 'warning'
                    : parseDiff?.class === 'down' ? 'success'
                    : null}
                />
                {/* PACK tile.
                    parse_diff is the authoritative source for "did the
                    pack change on THIS invoice?" — when present, override
                    the historical-window scan (which can read uniformly
                    null and produce "consistent across window" even when
                    the operator is staring at a real pack-size shift). */}
                {packChangeFromDiff ? (
                  <ContextCard
                    label="Pack"
                    value={`${packChangeFromDiff.from} \u2192 ${packChangeFromDiff.to}`}
                    secondary="Changed on this invoice"
                    tone="warning"
                  />
                ) : (
                  <ContextCard
                    label="Pack"
                    value={stats.currentPack || '\u2014'}
                    secondary={stats.distinctPacks.length > 1
                      ? `${stats.distinctPacks.length} variants in window`
                      : 'consistent across window'}
                    tone={stats.distinctPacks.length > 1 ? 'warning' : null}
                  />
                )}
                <ContextCard
                  label="Vendor"
                  value={stats.topVendor ? String(stats.topVendor).replace(/\n/g, ' \u00B7 ') : '\u2014'}
                  secondary={`${stats.dates.length} ${stats.dates.length === 1 ? 'order' : 'orders'} in window`}
                  valueClassName="text-sm"
                />
                <ContextCard
                  label="Cadence"
                  value={stats.avgGap != null
                    ? `~${stats.avgGap.toFixed(stats.avgGap < 10 ? 1 : 0)} days`
                    : '\u2014'}
                  secondary={stats.dates.length < 3 ? 'forming' : 'avg gap between orders'}
                />
              </div>

              {/* Metric toggle. Pills for metrics with no plottable data
                  in the rendered window render in a disabled state with a
                  hover tooltip — operator sees the option exists but
                  understands why it isn't available for this item. The
                  smart-default effect above never picks a disabled metric. */}
              <SegmentedPills
                value={metric || 'perLb'}
                onChange={setMetric}
                options={[
                  {
                    key: 'volume',
                    label: 'Volume (lb)',
                    // Volume needs weight_per_case OR (catch-weight + total_weight
                    // on the lines). When neither is available we keep the pill
                    // visible but disabled \u2014 operators sometimes scan items
                    // without pack weights and "the option exists, just not for
                    // this item" is a clearer signal than hiding it entirely.
                    disabled: !metricAvailability.volume,
                    disabledTitle: weightPerCase == null
                      ? 'No pack weight recorded on this item \u2014 Volume unavailable'
                      : 'No volume data on this metric for this item',
                  },
                  {
                    key: 'qty',
                    label: 'Qty',
                    disabled: !metricAvailability.qty,
                    disabledTitle: 'No quantity data on the lines in this window',
                  },
                  {
                    key: 'perLb',
                    label: '$/lb',
                    disabled: !metricAvailability.perLb,
                    disabledTitle: 'No $/lb data \u2014 this item likely isn\u2019t weight-priced',
                  },
                  {
                    key: 'spend',
                    label: 'Spend',
                    disabled: !metricAvailability.spend,
                    disabledTitle: 'No spend data on this metric for this item',
                  },
                ]}
                size="sm"
              />

              {/* Chart with current-invoice highlight + pack-change marker.
                  Only render once we've picked a metric — keeps the dummy
                  "No data" empty state from flashing on first paint. */}
              {metric && (
                <PremiumLineChart
                  series={chartSeries}
                  range={range}
                  priorRange={priorRange}
                  yFormat={yFormat}
                  gradientId="invoice-review-drillin-fill"
                  highlightDate={currentInvoiceDate}
                  highlightLabel="Current invoice"
                  markers={chartMarkers}
                  tooltipFormat={tooltipFormat}
                />
              )}

              {/* Per-invoice history table — current invoice highlighted */}
              <HistoryTable
                lines={lines}
                currentInvoiceId={currentInvoiceId}
                weightPerCase={weightPerCase}
                isCatchWeight={isCatchWeight}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function HistoryTable({ lines, currentInvoiceId, weightPerCase, isCatchWeight }) {
  // 7-column layout adds a TOTAL VOLUME column between Qty and $/cs so the
  // operator can sanity-check the line plot against the table without
  // hovering a chart point. "(estimated)" suffix appears for catch-weight
  // rows where total_weight is missing and we fell back to qty x weight_per_case.
  const grid = '88px 1.3fr 50px 76px 64px 64px 80px';
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8,
      }}>
        Per-invoice history
      </div>
      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
        <div className="grid text-xs" style={{
          gridTemplateColumns: grid,
          background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)',
        }}>
          {[
            { label: 'Date', align: 'left' },
            { label: 'Vendor', align: 'left' },
            { label: 'Qty', align: 'right' },
            { label: 'Total vol', align: 'right' },
            { label: '$/cs', align: 'right' },
            { label: '$/lb', align: 'right' },
            { label: 'Pack', align: 'left' },
          ].map(h => (
            <div key={h.label} className="px-3 py-2"
              style={{
                color: 'var(--text-secondary)', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                textAlign: h.align,
              }}
            >{h.label}</div>
          ))}
        </div>
        {lines.length === 0 ? (
          <div className="px-3 py-4 text-xs text-center" style={{ color: 'var(--text-muted)' }}>
            No invoices in this window.
          </div>
        ) : (
          lines.map((l, i) => {
            const prev = i > 0 ? lines[i - 1] : null;
            const perLb = l.price_per_lb != null ? Number(l.price_per_lb) : null;
            const prevPerLb = prev?.price_per_lb != null ? Number(prev.price_per_lb) : null;
            const perLbChanged = perLb != null && prevPerLb != null && Math.abs(perLb - prevPerLb) / prevPerLb >= 0.02;
            const perLbColor = !perLbChanged
              ? 'var(--text-primary)'
              : (perLb > prevPerLb ? 'var(--text-critical)' : 'var(--text-success)');
            const isCurrent = l.invoice_id === currentInvoiceId;
            const lineVol = computeLineVolumeLbs(l, weightPerCase, isCatchWeight);
            return (
              <div
                key={l.id}
                className="grid text-xs"
                style={{
                  gridTemplateColumns: grid,
                  borderBottom: i < lines.length - 1 ? '1px solid var(--border)' : 'none',
                  background: isCurrent
                    ? 'color-mix(in srgb, var(--accent-primary) 8%, transparent)'
                    : 'transparent',
                  borderLeft: isCurrent ? '3px solid var(--accent-primary)' : '3px solid transparent',
                }}
              >
                <div className="px-3 py-2" style={{
                  color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums',
                  fontWeight: isCurrent ? 600 : 400,
                }}>
                  {fmtDateShort(l.invoice_date)}
                  {isCurrent && (
                    <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      color: 'var(--accent-primary)' }}>
                      this
                    </span>
                  )}
                </div>
                <div className="px-3 py-2 truncate" style={{ color: 'var(--text-secondary)' }} title={l.vendor_name}>
                  {(l.vendor_name || '\u2014').replace(/\n/g, ' \u00B7 ')}
                </div>
                <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {Number(l.quantity || 0)}
                </div>
                <div className="px-3 py-2" style={{
                  color: lineVol.estimated ? 'var(--text-warning)' : 'var(--text-primary)',
                  fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                }}
                  title={lineVol.estimated ? 'Estimated: catch-weight line missing recorded weight; computed from qty \u00d7 pack weight' : undefined}
                >
                  {lineVol.lbs != null ? (
                    <>
                      {`${Math.round(lineVol.lbs)} lb`}
                      {lineVol.estimated && (
                        <span style={{
                          marginLeft: 4, fontSize: 9, fontWeight: 700,
                          letterSpacing: '0.04em', textTransform: 'uppercase',
                        }}>est</span>
                      )}
                    </>
                  ) : '\u2014'}
                </div>
                <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {l.unit_price != null ? `$${Number(l.unit_price).toFixed(2)}` : '\u2014'}
                </div>
                <div className="px-3 py-2" style={{
                  color: perLbColor, fontVariantNumeric: 'tabular-nums',
                  fontWeight: perLbChanged ? 600 : (isCurrent ? 600 : 400),
                  textAlign: 'right',
                }}>
                  {perLb != null ? `$${perLb.toFixed(2)}` : '\u2014'}
                </div>
                <div className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>
                  {fmtPack(l) || '\u2014'}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
